import React, { useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { fetchApi } from '../../../services/api';
import PartTagsInput from '../PartTagsInput';

interface CreatePartFormData {
  PartNumber: string;
  Description: string;
  Tags: string[];
  MinQuantity: number;
  MaxQuantity: number;
  UnitOfMeasure: string;
  Note: string;
}

interface CreatePartDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const defaultFormState: CreatePartFormData = {
  PartNumber: '',
  Description: '',
  Tags: [],
  MinQuantity: 0,
  MaxQuantity: 0,
  UnitOfMeasure: 'EA',
  Note: ''
};

const CreatePartDialog: React.FC<CreatePartDialogProps> = ({
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<CreatePartFormData>({ ...defaultFormState });
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePartFormData, string>>>({});

  const canSave = useMemo(() => {
    return (
      !loading &&
      form.PartNumber.trim().length > 0 &&
      form.UnitOfMeasure.trim().length > 0 &&
      form.MinQuantity >= 0 &&
      form.MaxQuantity >= 0
    );
  }, [form.PartNumber, form.UnitOfMeasure, form.MinQuantity, form.MaxQuantity, loading]);

  const validateForm = () => {
    const nextErrors: Partial<Record<keyof CreatePartFormData, string>> = {};

    if (!form.PartNumber.trim()) {
      nextErrors.PartNumber = 'Part number is required';
    }

    if (!form.UnitOfMeasure.trim()) {
      nextErrors.UnitOfMeasure = 'Unit of measure is required';
    }

    if (form.MinQuantity < 0) {
      nextErrors.MinQuantity = 'Minimum quantity cannot be negative';
    }

    if (form.MaxQuantity < 0) {
      nextErrors.MaxQuantity = 'Maximum quantity cannot be negative';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateField = <K extends keyof CreatePartFormData>(field: K, value: CreatePartFormData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    if (loading) {
      return;
    }

    setForm({ ...defaultFormState });
    setErrors({});
    onClose();
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const payload = {
      PartNumber: form.PartNumber.trim(),
      Description: form.Description.trim(),
      Tags: form.Tags.join(','),
      MinQuantity: form.MinQuantity,
      MaxQuantity: form.MaxQuantity,
      UnitOfMeasure: form.UnitOfMeasure.trim(),
      Note: form.Note.trim()
    };

    let shouldClose = false;

    try {
      await fetchApi('/api/ShopInventory/CreatePart', payload, 'POST');
      setForm({ ...defaultFormState });
      onSuccess();
      shouldClose = true;
    } catch (e: any) {
      if (!e?.status) alert('Failed to create part');
    } finally {
      setLoading(false);
      if (shouldClose) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      title="Create New Part"
      onClose={handleCancel}
      width={500}
      height={600}
    >
      <div className="k-form k-form-md">
        <div className="mb-3">
          <label className="form-label fw-bold">Part Number</label>
          <Input
            value={form.PartNumber}
            onChange={(e) => updateField('PartNumber', (e.value as string) ?? '')}
            disabled={loading}
          />
          {errors.PartNumber && <div className="text-danger small mt-1">{errors.PartNumber}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <Input
            value={form.Description}
            onChange={(e) => updateField('Description', (e.value as string) ?? '')}
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Tags</label>
          <PartTagsInput
            value={form.Tags}
            disabled={loading}
            onChange={(tags) => updateField('Tags', tags)}
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Minimum Quantity</label>
            <NumericTextBox
              value={form.MinQuantity}
              onChange={(e) => updateField('MinQuantity', e.value ?? 0)}
              min={0}
              format="n0"
              disabled={loading}
              style={{ width: '100%' }}
            />
            {errors.MinQuantity && <div className="text-danger small mt-1">{errors.MinQuantity}</div>}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Maximum Quantity</label>
            <NumericTextBox
              value={form.MaxQuantity}
              onChange={(e) => updateField('MaxQuantity', e.value ?? 0)}
              min={0}
              format="n0"
              disabled={loading}
              style={{ width: '100%' }}
            />
            {errors.MaxQuantity && <div className="text-danger small mt-1">{errors.MaxQuantity}</div>}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Unit of Measure</label>
          <Input
            value={form.UnitOfMeasure}
            onChange={(e) => updateField('UnitOfMeasure', (e.value as string) ?? '')}
            disabled={loading}
          />
          {errors.UnitOfMeasure && <div className="text-danger small mt-1">{errors.UnitOfMeasure}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Notes</label>
          <TextArea
            value={form.Note}
            onChange={(e) => updateField('Note', (e.value as string) ?? '')}
            rows={3}
            disabled={loading}
          />
        </div>
      </div>
      <DialogActionsBar>
        <Button
          type="button"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          themeColor="primary"
          disabled={!canSave}
          onClick={handleSave}
        >
          {loading ? 'Creating...' : 'Create Part'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CreatePartDialog;
